import {
  deleteListMember,
  getListMembers,
  upsertListMember,
} from '../../services/userLotListMembersService';
import {
  deleteCadProjectMember,
  getCadProjectMembers,
  upsertCadProjectMember,
} from '../../services/cadProjectMembersService';

export const SHARE_PERSON_ROLE_VIEWER = 'viewer';
export const SHARE_PERSON_ROLE_EDITOR = 'editor';

function normalizeEmail(value) {
  return String(value || '').trim().toLowerCase();
}

/**
 * Emails de `GET` miembros. Sin email válido se ignora la fila.
 *
 * @param {object} response
 * @returns {string[]}
 */
export function readMemberEmails(response) {
  const rows = Array.isArray(response?.data) ? response.data : [];
  const seen = new Set();
  const emails = [];
  rows.forEach((row) => {
    const email = normalizeEmail(row?.email);
    if (!email || seen.has(email)) return;
    seen.add(email);
    emails.push(email);
  });
  return emails;
}

/**
 * Allowlist + editores en una sola lista. El editor que no estaba en
 * la allowlist igual aparece (legado).
 *
 * @param {string[]} allowlistEmails
 * @param {string[]} memberEmails
 * @returns {string[]}
 */
export function mergeSharePeopleEmails(allowlistEmails, memberEmails) {
  const seen = new Set();
  const emails = [];
  [...(allowlistEmails || []), ...(memberEmails || [])].forEach((raw) => {
    const email = normalizeEmail(raw);
    if (!email || seen.has(email)) return;
    seen.add(email);
    emails.push(email);
  });
  return emails;
}

/**
 * @param {string[]} nextEditors
 * @param {string[]} prevEditors
 * @returns {{ toAdd: string[], toRemove: string[] }}
 */
export function diffEditorEmails(nextEditors, prevEditors) {
  const next = new Set((nextEditors || []).map(normalizeEmail).filter(Boolean));
  const prev = new Set((prevEditors || []).map(normalizeEmail).filter(Boolean));
  return {
    toAdd: [...next].filter((email) => !prev.has(email)),
    toRemove: [...prev].filter((email) => !next.has(email)),
  };
}

/**
 * Alta/baja de editores después de guardar la allowlist.
 *
 * @param {'list'|'cad'} kind
 * @param {string} collectionId
 * @param {string[]} nextEditorEmails
 * @param {string[]} prevEditorEmails
 */
export async function syncShareEditors(kind, collectionId, nextEditorEmails, prevEditorEmails) {
  const id = String(collectionId || '').trim();
  if (!id) return { ok: false, failed: [] };
  const { toAdd, toRemove } = diffEditorEmails(nextEditorEmails, prevEditorEmails);
  const failed = [];
  await Promise.all(toAdd.map(async (email) => {
    const res = kind === 'cad'
      ? await upsertCadProjectMember({ id, email })
      : await upsertListMember({ list_id: id, email });
    if (!res?._httpOk) failed.push(email);
  }));
  await Promise.all(toRemove.map(async (email) => {
    const res = kind === 'cad'
      ? await deleteCadProjectMember({ id, email })
      : await deleteListMember({ list_id: id, email });
    if (!res?._httpOk) failed.push(email);
  }));
  return { ok: failed.length === 0, failed };
}

/**
 * @param {'list'|'cad'} kind
 * @param {string} collectionId
 * @param {{ signal?: AbortSignal }} [options]
 */
export async function loadShareEditorEmails(kind, collectionId, { signal } = {}) {
  const id = String(collectionId || '').trim();
  if (!id) return [];
  const res = kind === 'cad'
    ? await getCadProjectMembers(id, { signal })
    : await getListMembers(id, { signal });
  if (!res?._httpOk) return [];
  return readMemberEmails(res);
}
